// ── THE SET-OUT CONTRACT (29 October 2026) ────────────────────────────────────────────────────────────────
// Jacky, on Cate Stewart's dining floor (J-1026-10308): *"this feature need to able cate to discuss with emma and try
// different layout, include to exclude border, and set border width to test visual, and the scale must be kept to real
// tile size over area"* — and *"pay attention to the area have out and in on the wall"*.
//
// One description of a tile set-out that the customer's laying view (apps/web) and EmmA's floor kit (emma-hub) both
// compute from, so a variant the customer tries in the browser is the same layout EmmA counts and the crew lays. Every
// figure is millimetres on the room's real outline — the polygon the laser measure saved, jogs and all — never a
// bounding box. The browser draws it instantly; EmmA's pass checks the rules and keeps the counts.
import { z } from 'zod';
const mm = z.number().finite();
export const SetOutColour = z.object({ id: z.enum(['a', 'b']), label: z.string().trim().min(1).max(60), hex: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    /** A photo swatch of the tile, when the Proposal holds one (a path under its own assets); the page fills tiles with it. */
    swatch: z.string().max(300).nullable().default(null) });
export const TileSetOut = z.object({ v: z.literal(1),
    tile: z.tuple([mm.positive().max(2000), mm.positive().max(2000)]),
    /** The joint between tiles: the grout, or the metal line a bevel-edged tile shows. */
    grout: mm.min(0).max(30).default(3), bevel: z.boolean().default(false), line: z.enum(['grout', 'gold']).default('grout'),
    colours: z.array(SetOutColour).min(1).max(2),
    /** Which colour sits on the datum tile; the other alternates from it in both directions. */
    colour_order: z.enum(['a', 'b']).default('a'),
    pattern: z.enum(['straight', 'diagonal']).default('diagonal'),
    /** Where the first tile sits: centred on the room, or a whole tile in the lowest corner of the laying frame. */
    datum: z.enum(['centre', 'corner']).default('centre'),
    // A zod default is handed back as given, never parsed, so the whole border is spelled out here (29 Oct 2026: `{}` left `on` undefined).
    border: z.object({ on: z.boolean().default(true), width_mm: mm.min(50).max(600).default(150), colour: z.enum(['a', 'b']).default('a') }).default({ on: true, width_mm: 150, colour: 'a' }),
    /** A transition trim where the floor meets another floor (a reducer up to a higher floor, a carpet edge at carpet): every open edge and every doorway. Never a brass inset: a 4.5 mm vinyl cannot hold one (Jacky, 3 Nov 2026). */
    trims: z.enum(['open_edges', 'none']).default('open_edges'),
    skirting: z.enum(['scotia', 'skirting', 'none']).default('scotia'),
    // ── Cate's rules, 1 November 2026 (Jacky, on her Dining) ──────────────────────────────────────────────────────────────
    /** A feature strip along one outline edge — Jacky's "one less hump": a black 50 mm vinyl strip along the deeper run of a
     *  shallow step brings that wall to one line, so the border runs straight and the step corners go. The edge moves inward by
     *  the strip's width before anything is laid; a neighbour that shrinks to nothing drops out. */
    strips: z.array(z.object({ edge: z.number().int().min(0), width_mm: mm.min(10).max(200), colour: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#111111') })).default([]),
    /** How the border turns where a wall meets a SHORT step (a nib, a jog under two border widths): `square` — *"go straight
     *  corner turn, instead of miter in"* — the wall strip runs straight through to the step line and the step piece butts into
     *  it; `mitre` keeps the 45° join there too. The room's own corners, two long walls meeting, are always mitred, and the
     *  border is ONE width all round so each mitre has two equal legs (*"corner miter join, keep two side equal length"*). */
    turns: z.enum(['square', 'mitre']).default('square'),
    /** The canvas save this set-out was tried on, so a layout from an older drawing is never mistaken for the current one. */
    save_number: z.number().int().positive().optional(), run_id: z.string().max(80).optional() });
export const defaultTileSetOut = (over = {}) => TileSetOut.parse({ v: 1, tile: [300, 300],
    colours: [{ id: 'a', label: 'Brown marble', hex: '#8C6A4E' }, { id: 'b', label: 'White marble', hex: '#F2EFE9' }], ...over });
const r1 = (n) => Math.round(n * 10) / 10;
const r2 = (n) => Math.round(n * 100) / 100;
/** Signed area: positive when the points run anticlockwise in a y-up frame (clockwise on screen, where y is down). */
export function signedArea(pts) { let s = 0; for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += a[0] * b[1] - b[0] * a[1];
} return s / 2; }
export const polygonArea = (pts) => Math.abs(signedArea(pts));
const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const len = (v) => Math.hypot(v[0], v[1]);
function lineMeet(p, d, q, e) { const den = d[0] * e[1] - d[1] * e[0]; if (Math.abs(den) < 1e-9)
    return null; const t = ((q[0] - p[0]) * e[1] - (q[1] - p[1]) * e[0]) / den; return [p[0] + t * d[0], p[1] + t * d[1]]; }
/** The outline moved inward by `w` on every edge, corners mitred: the field the whole tiles are laid in once a border
 *  of that width runs around the room. Jogs stay jogs; a corner two edges cannot meet at keeps its offset point. */
export function insetPolygon(outline, w) {
    const n = outline.length;
    if (n < 3 || w <= 0)
        return outline.slice();
    // With y down the screen, a ring with positive signed area runs clockwise and its inside is to the LEFT of each edge
    // travelled, which is the (-dy, dx) side; a ring the other way round needs the opposite normal.
    const inward = signedArea(outline) > 0 ? 1 : -1;
    const lines = outline.map((p, i) => { const q = outline[(i + 1) % n], d = sub(q, p), L = len(d) || 1, nx = inward * -d[1] / L, ny = inward * d[0] / L; return { p: [p[0] + nx * w, p[1] + ny * w], d }; });
    return lines.map((l, i) => { const prev = lines[(i - 1 + n) % n]; return lineMeet(prev.p, prev.d, l.p, l.d) ?? l.p; });
}
/** A convex polygon (a tile) clipped to the room: Sutherland–Hodgman with the TILE as the clip window, so a room with
 *  jogs, which is not convex, is the subject. The result can touch itself at a jog; its area and its drawing are right. */
export function clipToConvex(subject, clip) {
    const sign = signedArea(clip) >= 0 ? 1 : -1;
    let out = subject.slice();
    for (let i = 0; i < clip.length && out.length; i++) {
        const a = clip[i], b = clip[(i + 1) % clip.length], inp = out;
        out = [];
        const inside = (p) => sign * ((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0])) >= -1e-7;
        for (let j = 0; j < inp.length; j++) {
            const cur = inp[j], prev = inp[(j - 1 + inp.length) % inp.length], ci = inside(cur), pi = inside(prev);
            if (ci) {
                if (!pi) {
                    const m = lineMeet(prev, sub(cur, prev), a, sub(b, a));
                    if (m)
                        out.push(m);
                }
                out.push(cur);
            }
            else if (pi) {
                const m = lineMeet(prev, sub(cur, prev), a, sub(b, a));
                if (m)
                    out.push(m);
            }
        }
    }
    return out;
}
const rot = (p, c, s) => [p[0] * c - p[1] * s, p[0] * s + p[1] * c];
/** The part of edge [a,b] between `from` and `to` mm along it. */
const span = (a, b, from, to) => { const L = len(sub(b, a)) || 1, u = [(b[0] - a[0]) / L, (b[1] - a[1]) / L]; return [[a[0] + u[0] * from, a[1] + u[1] * from], [a[0] + u[0] * to, a[1] + u[1] * to]]; };
/** The room with its feature strips laid: each named edge moved inward by the strip's width, neighbours that vanish dropped.
 *  `map[k]` is the original edge the laid edge `k` came from (its wall, doors and openness travel with it); `shifted[k]` is
 *  how far that edge's start moved along it, so a door keeps its place. */
export function applyStrips(room, strips) {
    const n = room.outline_mm.length;
    let outline = room.outline_mm.slice(), map = outline.map((_, k) => k), shifted = outline.map(() => 0);
    const laid = [];
    const inward = signedArea(room.outline_mm) > 0 ? 1 : -1;
    for (const st of strips) {
        const k = map.indexOf(st.edge);
        if (k < 0 || outline.length < 4)
            continue;
        const p = outline[k], q = outline[(k + 1) % outline.length], d = sub(q, p), L = len(d);
        if (L < 1)
            continue;
        const u = [d[0] / L, d[1] / L], nv = [inward * -u[1] * st.width_mm, inward * u[0] * st.width_mm];
        laid.push({ edge: st.edge, quad: [p, q, [q[0] + nv[0], q[1] + nv[1]], [p[0] + nv[0], p[1] + nv[1]]], length_mm: r1(L), width_mm: st.width_mm, colour: st.colour });
        const next = outline.map((pt, i) => i === k || i === (k + 1) % outline.length ? [pt[0] + nv[0], pt[1] + nv[1]] : pt);
        // A neighbour that now has no length is gone; the one beyond it keeps its identity.
        const keep = next.map((pt, i) => len(sub(next[(i + 1) % next.length], pt)) >= 1);
        const after = (k + 1) % outline.length;
        const o2 = [], m2 = [], s2 = [];
        for (let i = 0; i < next.length; i++) {
            if (!keep[i])
                continue;
            o2.push(next[i]);
            m2.push(map[i]);
            s2.push(shifted[i]);
        }
        // The edge after the strip (if it survives) starts further along itself by the strip width; the one before ends earlier — only the start matters for doors.
        if (keep[after]) {
            const j = m2.indexOf(map[after]);
            if (j >= 0)
                s2[j] = (shifted[after] ?? 0) + st.width_mm;
        }
        outline = o2;
        map = m2;
        shifted = s2;
    }
    return { outline, map, shifted, strips: laid };
}
/** Nibs no deeper than the border are passed straight (Jacky, 1 Nov 2026: *"is it possible just go pass without border in
 *  nibs"* — Cate's nib is 150 deep by tape, flush with the 150 mm border). A nib is three edges: a step into the room, a face,
 *  a step back, the two steps parallel and each no longer than the border, the first corner convex and the next reflex.
 *  The two face corners are removed, so the wall line runs straight through; the nib comes back as a quad to draw as wall. */
export function passNibs(outline, map, shifted, bw) {
    const nibs = [];
    if (!bw || outline.length < 6)
        return { outline, map, shifted, nibs };
    const ring = signedArea(outline) > 0 ? 1 : -1;
    let o = outline.slice(), m = map.slice(), sh = shifted.slice();
    for (let guard = 0; guard < 16; guard++) {
        const n = o.length;
        let found = -1;
        for (let k = 0; k < n && found < 0; k++) {
            const P0 = o[k], P1 = o[(k + 1) % n], P2 = o[(k + 2) % n], P3 = o[(k + 3) % n], Pm = o[(k - 1 + n) % n];
            const d0 = sub(P0, Pm), d1 = sub(P1, P0), d2 = sub(P2, P1), d3 = sub(P3, P2), L1 = len(d1), L3 = len(d3);
            if (L1 < 1 || L3 < 1 || L1 > bw + 0.5 || L3 > bw + 0.5)
                continue;
            if ((d1[0] * d3[0] + d1[1] * d3[1]) / (L1 * L3) > -0.98)
                continue;
            const t1 = d0[0] * d1[1] - d0[1] * d1[0], t2 = d1[0] * d2[1] - d1[1] * d2[0];
            if (!((t1 > 0) === (ring > 0)) || (t2 > 0) === (ring > 0))
                continue; // into the room, then along its face
            found = k;
            nibs.push({ quad: [P0, P1, P2, P3], depth_mm: r1(Math.max(L1, L3)), face_mm: r1(len(d2)), edges: [m[k], m[(k + 1) % n], m[(k + 2) % n]] });
        }
        if (found < 0)
            break;
        const drop = new Set([(found + 1) % n, (found + 2) % n]);
        o = o.filter((_, i) => !drop.has(i));
        m = m.filter((_, i) => !drop.has(i));
        sh = sh.filter((_, i) => !drop.has(i));
    }
    return { outline: o, map: m, shifted: sh, nibs };
}
/** The whole set-out on one room: the field tiles clipped to the inside of the border, the border run on every edge,
 *  the trims and the skirting, and the counts the customer reads. */
export function tileSetOut(room, setout) {
    const s = TileSetOut.parse(setout);
    const stripped = applyStrips(room, s.strips);
    const laid = { ...passNibs(stripped.outline, stripped.map, stripped.shifted, s.border.on ? s.border.width_mm : 0), strips: stripped.strips }, outline = laid.outline;
    // The laid edges carry the room's own description (wall, openness, doors) across strips and passed nibs; an edge the room
    // does not describe gets neither trim nor skirting, as before.
    const edges = outline.flatMap((p, k) => {
        const src = room.edges.find(e => e.index === laid.map[k]);
        if (!src)
            return [];
        const q = outline[(k + 1) % outline.length], off = laid.shifted[k] ?? 0;
        return [{ index: k, from_mm: p, to_mm: q, wall_id: src.wall_id, open: src.open, doors: src.doors.map(d => ({ width_mm: d.width_mm, at_mm: d.at_mm - off })) }];
    });
    const bw = s.border.on ? s.border.width_mm : 0, inner = bw ? insetPolygon(outline, bw) : outline.slice();
    const [tw, th] = s.tile, g = s.grout, px = tw + g, py = th + g, ang = (s.pattern === 'diagonal' ? 45 : 0) * Math.PI / 180, c = Math.cos(ang), sn = Math.sin(ang);
    // The laying frame: the room turned by the angle; grid cell (i, j) is the tile at (ox + i·px, oy + j·py) in that frame.
    const lay = inner.map(p => rot(p, c, -sn));
    const xs = lay.map(p => p[0]), ys = lay.map(p => p[1]), x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const ox = s.datum === 'centre' ? (x0 + x1) / 2 - tw / 2 : x0, oy = s.datum === 'centre' ? (y0 + y1) / 2 - th / 2 : y0;
    const i0 = Math.floor((x0 - ox) / px) - 1, i1 = Math.ceil((x1 - ox) / px) + 1, j0 = Math.floor((y0 - oy) / py) - 1, j1 = Math.ceil((y1 - oy) / py) + 1;
    const tileArea = tw * th, second = s.colour_order === 'a' ? 'b' : 'a', colourOf = (i, j) => (((i + j) % 2) + 2) % 2 === 0 ? s.colour_order : second;
    const twoColours = s.colours.length > 1;
    const field = [];
    let smallest = null;
    for (let j = j0; j <= j1; j++)
        for (let i = i0; i <= i1; i++) {
            const lx = ox + i * px, ly = oy + j * py;
            if (lx > x1 || ly > y1 || lx + tw < x0 || ly + th < y0)
                continue;
            const square = [[lx, ly], [lx + tw, ly], [lx + tw, ly + th], [lx, ly + th]].map(p => rot(p, c, sn));
            const piece = clipToConvex(inner, square), area = polygonArea(piece);
            if (piece.length < 3 || area < 1)
                continue;
            const full = area >= tileArea * 0.999;
            if (!full) {
                const b = bounds(piece);
                const small = Math.min(b.w, b.h);
                smallest = smallest === null ? small : Math.min(smallest, small);
            }
            field.push({ corners: full ? square : piece, colour: twoColours ? colourOf(i, j) : 'a', full, area_mm2: r1(area), ij: [i, j] });
        }
    // The border: one strip per edge between the outline and the inset, cut from tiles in pieces a tile long.
    // Jacky, 1 Nov 2026 (Cate's nib): where a wall meets a SHORT step the border turns SQUARE, not mitred — the longer strip runs
    // straight through to the step line and the step piece butts into it. A step shorter than the border vanishes under the
    // through strip, and the strip beyond it (the nib face) extends square to the field line. Two long walls keep their mitre.
    const inwardSign = signedArea(outline) > 0 ? 1 : -1, N = outline.length;
    const startTrim = new Array(N).fill(0), endTrim = new Array(N).fill(0), sqS = new Array(N).fill(false), sqE = new Array(N).fill(false), through = new Array(N).fill(false);
    if (bw && s.turns === 'square' && N >= 4) {
        const E = outline.map((p, k) => { const q = outline[(k + 1) % N]; return { p, q, L: len(sub(q, p)) }; });
        for (let k = 0; k < N; k++) {
            const a = (k - 1 + N) % N, b = k, da = sub(E[a].q, E[a].p), db = sub(E[b].q, E[b].p), turn = da[0] * db[1] - da[1] * db[0];
            const convex = (turn > 0) === (inwardSign > 0), step = Math.min(E[a].L, E[b].L) < 2 * bw;
            if (!step || Math.abs(turn) < 1e-6)
                continue;
            if (convex) { // the wall meets the step: the longer strip runs through to the step line, the step piece starts a border width in
                if (E[a].L >= E[b].L) {
                    sqE[a] = true;
                    through[a] = true;
                    startTrim[b] += bw;
                    sqS[b] = true;
                }
                else {
                    sqS[b] = true;
                    through[b] = true;
                    endTrim[a] += bw;
                    sqE[a] = true;
                }
            }
            else { // the step meets the face (a reflex corner): the longer strip extends a border width past the corner, square; the step piece ends square at it
                if (E[a].L >= E[b].L) {
                    endTrim[a] -= bw;
                    sqE[a] = true;
                    sqS[b] = true;
                }
                else {
                    startTrim[b] -= bw;
                    sqS[b] = true;
                    sqE[a] = true;
                }
            }
        }
        // A step piece with no length left is gone; the strip on its far side, if the reflex rule did not already extend it, extends a border width back.
        for (let k = 0; k < N; k++) {
            if (E[k].L - startTrim[k] - endTrim[k] > 1)
                continue;
            if (startTrim[k] > 0) {
                const c2 = (k + 1) % N;
                if (startTrim[c2] >= 0) {
                    startTrim[c2] != -bw;
                    sqS[c2] = true;
                }
            }
            else if (endTrim[k] > 0) {
                const c2 = (k - 1 + N) % N;
                if (endTrim[c2] >= 0) {
                    endTrim[c2] != -bw;
                    sqE[c2] = true;
                }
            }
        }
    }
    const border = bw ? outline.flatMap((p0, k) => {
        const q0 = outline[(k + 1) % N], ip = inner[k], iq = inner[(k + 1) % N], L0 = len(sub(q0, p0)) || 1;
        const u = [(q0[0] - p0[0]) / L0, (q0[1] - p0[1]) / L0], n = [inwardSign * -u[1], inwardSign * u[0]], L = L0 - startTrim[k] - endTrim[k];
        if (L <= 1)
            return [];
        const p = [p0[0] + u[0] * startTrim[k], p0[1] + u[1] * startTrim[k]], q = [q0[0] - u[0] * endTrim[k], q0[1] - u[1] * endTrim[k]];
        const pi = sqS[k] ? [p[0] + n[0] * bw, p[1] + n[1] * bw] : ip, qi = sqE[k] ? [q[0] + n[0] * bw, q[1] + n[1] * bw] : iq;
        const cuts = [];
        for (let d = tw; d < L - 1; d += tw) {
            const o = [p[0] + u[0] * d, p[1] + u[1] * d];
            cuts.push([o, [o[0] + n[0] * bw, o[1] + n[1] * bw]]);
        }
        return [{ edge: k, a: p, b: q, length_mm: r1(L), quad: [p, q, qi, pi], pieces: Math.ceil(L / tw), cuts, through: through[k] }];
    }) : [];
    const stripsPerTile = bw ? Math.max(1, Math.floor(th / bw)) : 0, borderPieces = border.reduce((n, b) => n + b.pieces, 0);
    // Trims at open edges and doorways; skirting on every edge that is a wall, less its doorways.
    const trims = [], skirting = [];
    for (const e of edges) {
        const L = len(sub(e.to_mm, e.from_mm));
        if (L < 1)
            continue;
        if (e.open === 'open_plan') {
            if (s.trims !== 'none')
                trims.push({ edge: e.index, a: e.from_mm, b: e.to_mm, length_mm: r1(L), kind: 'open' });
            continue;
        }
        if (e.open === 'robe_face')
            continue;
        const doors = e.doors.map(d => [Math.max(0, d.at_mm - d.width_mm / 2), Math.min(L, d.at_mm + d.width_mm / 2)]).sort((x, y) => x[0] - y[0]);
        if (s.trims !== 'none')
            for (const [f, t] of doors) {
                const [a, b] = span(e.from_mm, e.to_mm, f, t);
                trims.push({ edge: e.index, a, b, length_mm: r1(t - f), kind: 'door' });
            }
        if (s.skirting === 'none')
            continue;
        let at = 0;
        for (const [f, t] of doors) {
            if (f > at + 1) {
                const [a, b] = span(e.from_mm, e.to_mm, at, f);
                skirting.push({ edge: e.index, a, b, length_mm: r1(f - at) });
            }
            at = Math.max(at, t);
        }
        if (L > at + 1) {
            const [a, b] = span(e.from_mm, e.to_mm, at, L);
            skirting.push({ edge: e.index, a, b, length_mm: r1(L - at) });
        }
    }
    const tiles = { a: { full: 0, cut: 0 }, b: { full: 0, cut: 0 } };
    for (const t of field)
        tiles[t.colour][t.full ? 'full' : 'cut']++;
    const full = tiles.a.full + tiles.b.full, cut = tiles.a.cut + tiles.b.cut;
    const counts = { tiles, total: full + cut, full, cut, smallest_cut_mm: smallest === null ? null : Math.round(smallest),
        area_m2: r2(polygonArea(room.outline_mm) / 1e6), field_area_m2: r2(polygonArea(inner) / 1e6),
        border_lm: r2(border.reduce((n, b) => n + b.length_mm, 0) / 1000), border_pieces: borderPieces, border_tiles: stripsPerTile ? Math.ceil(borderPieces / stripsPerTile) : 0,
        trim_lm: r2(trims.reduce((n, t) => n + t.length_mm, 0) / 1000), skirting_lm: r2(skirting.reduce((n, t) => n + t.length_mm, 0) / 1000), strip_lm: r2(laid.strips.reduce((n, t) => n + t.length_mm, 0) / 1000) };
    return { outline, inner, field, border, strips: laid.strips, nibs: laid.nibs, trims, skirting, counts };
}
export function bounds(points, margin = 0) {
    if (!points.length)
        return { x: 0, y: 0, w: 1000, h: 1000 };
    const xs = points.map(p => p[0]), ys = points.map(p => p[1]), x0 = Math.min(...xs) - margin, y0 = Math.min(...ys) - margin;
    return { x: x0, y: y0, w: Math.max(...xs) + margin - x0, h: Math.max(...ys) + margin - y0 };
}
/** A variant in words, for the customer's message to EmmA and for the log: the same line in any language is built by the page. */
export const setOutKey = (s) => `${s.pattern}|${s.colour_order}|${s.datum}|${s.border.on ? `border:${s.border.width_mm}:${s.border.colour}` : 'no-border'}|${s.trims}|${s.skirting}`
    // The 1 Nov 2026 fields ride only when they say something, so every key from before reads the same.
    + (s.strips.length ? `|strips:${s.strips.map(x => `${x.edge}:${x.width_mm}`).join(',')}` : '') + (s.turns !== 'square' ? `|turns:${s.turns}` : '');
export function stripCandidates(room, maxStep = 80) {
    const o = room.outline_mm, n = o.length, out = [];
    if (n < 6)
        return out;
    for (let k = 0; k < n; k++) {
        const e = room.edges.find(x => x.index === k);
        if (!e || e.open)
            continue;
        const L = len(sub(o[(k + 1) % n], o[k]));
        if (L < 10 || L > maxStep)
            continue;
        for (const run of [(k - 1 + n) % n, (k + 1) % n]) {
            const re = room.edges.find(x => x.index === run);
            if (!re || re.open)
                continue;
            const laid = applyStrips(room, [{ edge: run, width_mm: Math.round(L), colour: '#111111' }]);
            if (laid.outline.length <= n - 1 && !laid.map.includes(k)) {
                out.push({ step_edge: k, run_edge: run, width_mm: Math.round(L), run_length_mm: r1(len(sub(o[(run + 1) % n], o[run]))) });
                break;
            }
        }
    }
    return out;
}
//# sourceMappingURL=tile-setout.js.map